<template>
  <div id="package-usage-history" class="w-full flex flex-col gap-5">
    <div class="flex flex-col bg-pure-white rounded-2xl">
      <div
        class="sticky top-30 z-30 py-4 px-6 flex items-center justify-between bg-pure-white rounded-t-2xl"
      >
        <BlTextTitle4 text="Usage history" />
        <BlSort
          class="w-60"
          v-model="sortInput"
          :options="sortOptions"
          :defaultValue="defaultSort"
          @change="onChangeSort($event)"
        />
      </div>

      <div class="flex flex-col items-center">
        <div v-if="fetchLoading" class="h-128 grid place-items-center">
          <div class="blum-loader"></div>
        </div>
        <div
          v-else-if="!transactions.length"
          class="px-6 h-128 flex flex-col items-center justify-center"
        >
          <BlTextBody1
            class="text-center"
            text="This package hasn't been used in any appointments yet. Once it's assigned, the usage history will be displayed here."
          />
        </div>
        <div v-else class="mb-8">
          <div
            id="package-usage-history-header"
            class="sticky top-48 z-20 flex items-center flex-grow rounded-t"
          >
            <BlTooltip
              class="w-25 h-12 px-4 flex items-center flex-shrink-0 bg-slate-300 border-t border-slate-400 rounded-tl border-l"
            >
              <template v-slot:tooltip>
                <div class="w-25">Transaction ID</div>
              </template>
              <BlTextSubtitle3 text="Transaction ID" class="truncate" />
            </BlTooltip>
            <BlTooltip
              class="w-40 h-12 px-4 flex items-center flex-shrink-0 bg-slate-300 border-t border-slate-400"
            >
              <BlTextSubtitle3 text="Transaction date" class="truncate" />
            </BlTooltip>
            <BlTooltip
              class="w-40 h-12 px-4 flex items-center flex-shrink-0 bg-slate-300 border-t border-slate-400"
            >
              <BlTextSubtitle3 text="Client name" class="truncate" />
            </BlTooltip>
            <BlTooltip
              class="w-40 h-12 px-4 flex items-center flex-shrink-0 bg-slate-300 border-t border-slate-400"
            >
              <BlTextSubtitle3 text="Added by" class="truncate" />
            </BlTooltip>
            <div
              class="w-25 h-12 px-4 flex items-center flex-shrink-0 bg-slate-300 border-t border-slate-400 rounded-tr border-r"
            >
              <BlTextSubtitle3 text="" />
            </div>
          </div>

          <div
            v-for="(transaction, idx) in transactions"
            :key="transaction.id"
            class="flex h-13"
          >
            <div
              class="w-25 px-4 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400 border-l"
              :class="{ 'rounded-bl': idx + 1 === transactions.length }"
            >
              <BlTextBody2
                :title="transaction.id"
                :text="transaction.id"
                class="truncate"
              />
            </div>
            <div
              class="w-40 px-4 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400"
            >
              <BlTextBody2
                :text="
                  displayDate({
                    date: transaction.created_at,
                    format: 'DD MMM YYYY HH:mm',
                    empty: ''
                  })
                "
                class="truncate"
              />
            </div>
            <div
              class="w-40 px-4 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400"
            >
              <BlTextBody2 v-if="!transaction.client" text="-" />
              <BlTextBody2
                v-else
                :title="transaction.client.name"
                :text="transaction.client.name"
                class="truncate"
              />
            </div>
            <div
              class="w-40 px-4 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400"
            >
              <BlTextBody2 v-if="!transaction.user" text="-" />
              <BlTextBody2
                v-else
                :title="transaction.user.name || 'Deleted user'"
                :text="transaction.user.name || 'Deleted user'"
                class="truncate"
                :class="{ ' italic': !transaction.user.name }"
              />
            </div>
            <div
              class="w-25 flex items-center flex-shrink-0 bg-pure-white border-b border-slate-400 border-r"
              :class="{ 'rounded-br': idx + 1 === transactions.length }"
            >
              <router-link
                v-if="transaction.client"
                :to="{
                  name: 'ClientTab',
                  params: {
                    id: transaction.client.id,
                    tab: 'credit',
                    redirect_target: `/center/package/${$route.params.id}/history`,
                    redirect_label: 'Package usage history'
                  }
                }"
              >
                <BlInputButton label="View client" type="outlined" dense />
              </router-link>
            </div>
          </div>
        </div>

        <div v-if="totalTransactions > 25" class="px-6 w-full mb-36">
          <BlInputPagination
            :page="page"
            :perPage="perPage"
            :total="totalTransactions"
            @changed="changePage"
            @changed-per-page="changePerPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate } from "@/plugins/helper.js";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlChip from "../../BlChip.vue";
import BlTooltip from "../../BlTooltip.vue";
import BlSort from "../../BlSort.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputPagination from "../../BlInputPagination.vue";

export default {
  name: "PackageUsageHistory",
  components: {
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlIcon,
    BlChip,
    BlTooltip,
    BlSort,
    BlInputButton,
    BlInputPagination
  },
  data() {
    const defaultSort = { primary: "date", secondary: "desc" };
    return {
      defaultSort,
      fetchLoading: false,
      transactions: [],
      totalTransactions: [],
      page: 1,
      perPage: 25,
      sortInput: defaultSort,
      sortOptions: {
        primary: [
          { value: "id", label: "Transaction ID" },
          { value: "date", label: "Transaction date" },
          { value: "name", label: "Client name" }
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "creditPackage"]),
    getParams() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const sort = this.sortInput;

      /**
       * - sort mapping -
       * id_asc -- id_desc
       * newest -- oldest
       * name_asc -- name_desc
       */
      // prettier-ignore
      const sorts = {
        id: { asc: "id_asc", desc: "id_desc" },
        date: { asc: "newest", desc: "oldest" },
        name: { asc: "name_asc", desc: "name_desc" }
      };
      params += `&sort=${sorts[sort.primary][sort.secondary]}`;

      return params;
    }
  },
  async mounted() {
    const searchParams = this.$route.query;
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }

    if (searchParams["sort"]) {
      const temp = searchParams["sort"] || "";
      // prettier-ignore
      const sorts = {
        id_asc: { primary: "id", secondary: "asc" },
        id_desc: { primary: "id", secondary: "desc" },
        newest: { primary: "date", secondary: "asc" },
        oldest: { primary: "date", secondary: "desc" },
        name_asc: { primary: "name", secondary: "asc" },
        name_desc: { primary: "name", secondary: "desc" }
      };
      if (temp && sorts[temp]) {
        this.sortInput = sorts[temp];
      }
    }

    this.fetchPackageUsageHistory();
  },
  methods: {
    ...mapActions(["actionGet"]),
    displayDate,
    //
    async fetchPackageUsageHistory() {
      const id = this.$route.params.id;
      const params = {
        url: `/api/v1/packages/${id}/usage_history${this.getParams}`
      };

      this.fetchLoading = true;
      const { success, data } = await this.actionGet(params);
      this.fetchLoading = false;
      if (!success) return;

      this.transactions = data.credit_transactions;
      this.totalTransactions = data.total_count;

      // sync url path for page
      const incomingPath = `/center/package/${id}/history${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchPackageUsageHistory();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchPackageUsageHistory();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchPackageUsageHistory();
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/center/package/PackageUsageHistory.vue